import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { workOrderApi } from '../../api/workOrderApi';
import FileAttachment from '../../components/FileAttachment/FileAttachment';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar';
import tiptapExtensions from '../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop';
import './WorkOrderFormModal.css';
import WorkOrderFormModal from './WorkOrderFormModal';

function WorkOrderReportFormModal({
  open = true,
  isSubModal = false,
  workOrderId,
  workOrderTitle = '',
  workOrder = null,
  currentProgress = 0,
  report = null,
  reportSeq = 1,
  onClose,
  onSubmitted,
  onSaved
}) {
  const isDraftEdit = report?.approval_status === 'draft' || report?.approval_status === 'temp';
  const isRejectEdit = report?.approval_status === 'rejected';

  // 진척도 조회 모드(변경 불가) 조건: 1) 업무지시가 종료/완료/중단/취소되었거나 2) 보고서가 이미 승인완료된 경우
  const resolvedWorkOrder = workOrder || report?.work_order || report?.workOrder || null;
  const isWorkOrderClosed = ['completed', 'done', 'stop', 'cancel', 'canceled'].includes(resolvedWorkOrder?.status);
  const isReportApproved = report?.approval_status === 'approved' || report?.approval_status === 'completed';
  const isProgressReadOnly = isWorkOrderClosed || isReportApproved;

  const targetTitle = workOrderTitle || workOrder?.title || '';

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr).substring(0, 10);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return String(dateStr).substring(0, 10);
    }
  };

  const startDateStr = formatDateOnly(resolvedWorkOrder?.start_date);
  const dueDateStr = formatDateOnly(resolvedWorkOrder?.due_date);
  const periodText = (startDateStr && dueDateStr)
    ? `${startDateStr} ~ ${dueDateStr}`
    : (startDateStr || dueDateStr || '-');

  const [reportTitle, setReportTitle] = useState('');
  const [progressRate, setProgressRate] = useState(0);
  const [isFinalReport, setIsFinalReport] = useState(false);
  const [prevInterimProgress, setPrevInterimProgress] = useState(0);
  const [isPublic, setIsPublic] = useState(true);
  const [files, setFiles] = useState([]);
  const [editorHeight, setEditorHeight] = useState('400px');
  const [submitting, setSubmitting] = useState(false);
  const [viewWorkOrderOpen, setViewWorkOrderOpen] = useState(false);

  const editor = useEditorWithDragDrop(tiptapExtensions);

  const activeSeq = report?.report_seq || reportSeq || 1;

  useEffect(() => {
    if (report) {
      // 기존 제목에서 앞쪽 시스템 태그만 분리하여 순수 제목만 인풋에 표시 (사용자의 [긴급] 등은 온전히 보존)
      const rawTitle = report.report_title || targetTitle || '';
      const pureTitle = rawTitle.replace(/^(\[\d+차\s*(?:결과)?보고\]|\[최종\s*(?:결과)?보고\]|\[\d+차\s*결과\]|\[최종결과\]|\[\d+차\s*보완\]|\[최종\s*보완\])\s*/, '');
      setReportTitle(pureTitle);

      let initProgress = report.progress_rate !== undefined && report.progress_rate !== null
        ? Number(report.progress_rate)
        : (currentProgress ?? 0);
      let initIsFinal = Boolean(report.is_final || initProgress === 100);

      // [버그 2 해결] 보완 수정 모드(isRejectEdit)일 때는 100% 최종 상태를 해제하고 90% 보완 상태로 리셋
      if (isRejectEdit && initProgress >= 100) {
        initProgress = (currentProgress && currentProgress < 100) ? currentProgress : 90;
        initIsFinal = false;
      }

      setIsFinalReport(initIsFinal);
      setProgressRate(initProgress);
      setPrevInterimProgress(initProgress < 100 ? initProgress : (currentProgress < 100 ? currentProgress : 80));
      setIsPublic(report.is_public !== false);

      if (editor) {
        setTimeout(() => editor.commands.setContent(report.report_content || ''), 100);
      }
      if (report.files) {
        setFiles((report.files || []).map(f => ({
          id: f.id,
          name: f.file_name || f.originalName || f.name,
          originalName: f.file_name || f.originalName || f.name,
          size: f.file_size ?? f.size ?? 0,
          path: f.file_path || f.path || '',
          isNew: false
        })));
      } else {
        setFiles([]);
      }
    } else {
      setReportTitle(targetTitle || '');
      const initProgress = currentProgress ?? 0;
      const initIsFinal = initProgress === 100;

      setIsFinalReport(initIsFinal);
      setProgressRate(initProgress);
      setPrevInterimProgress(initProgress < 100 ? initProgress : 80);
      setIsPublic(true);

      if (editor) {
        setTimeout(() => editor.commands.setContent(''), 100);
      }
      setFiles([]);
    }
  }, [open, report, targetTitle, editor, currentProgress, reportSeq, isRejectEdit]);

  // 드롭다운 변경 핸들러
  const handleReportTypeChange = (val) => {
    if (isProgressReadOnly) return;
    if (val === 'final') {
      if (progressRate < 100) {
        setPrevInterimProgress(progressRate);
      }
      setIsFinalReport(true);
      setProgressRate(100);
    } else {
      setIsFinalReport(false);
      const restored = prevInterimProgress > 0 && prevInterimProgress < 100
        ? prevInterimProgress
        : (currentProgress < 100 ? currentProgress : 90);
      setProgressRate(restored);
    }
  };

  // 슬라이더/인풋 변경 핸들러 (양방향 연동)
  const handleProgressChange = (val) => {
    if (isProgressReadOnly) return;
    const clamped = Math.min(100, Math.max(0, val));
    setProgressRate(clamped);
    if (clamped === 100) {
      if (!isFinalReport) {
        setPrevInterimProgress(progressRate < 100 ? progressRate : prevInterimProgress);
        setIsFinalReport(true);
      }
    } else {
      if (isFinalReport) {
        setIsFinalReport(false);
      }
      setPrevInterimProgress(clamped);
    }
  };

  if (!open) return null;

  const handleSave = async (isDraftAction = false) => {
    const htmlContent = editor ? editor.getHTML() : '';
    const textContent = editor ? editor.getText().trim() : '';

    if (!isDraftAction) {
      if (!reportTitle.trim()) return alert('처리결과 제목을 입력해 주세요.');
      if (!textContent) return alert('처리결과 상세 내용을 입력해 주세요.');
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      if (report?.id) {
        formData.append('report_id', report.id);
      }

      // 순수 제목 추출 및 저장 (제목에 [1차 결과보고] 등 불필요한 접두사를 붙이지 않고 순수 제목만 보관)
      let rawTitle = reportTitle.trim() || targetTitle || '처리결과';
      rawTitle = rawTitle.replace(/^(\[\d+차\s*(?:결과)?보고\]|\[최종\s*(?:결과)?보고\]|\[\d+차\s*결과\]|\[최종결과\]|\[\d+차\s*보완\]|\[최종\s*보완\]|\[결과보고\]|\[처리결과\])\s*/, '');
      const titleToSave = rawTitle;

      formData.append('report_title', titleToSave);
      formData.append('report_content', htmlContent);
      formData.append('is_public', isPublic ? '1' : '0');
      formData.append('is_final', isFinalReport ? '1' : '0');
      formData.append('approval_status', isDraftAction ? 'draft' : 'submitted');
      formData.append('progress_rate', isProgressReadOnly ? (report?.progress_rate ?? currentProgress ?? 100) : progressRate);

      // 기존 파일 중 유지할 ID 목록 전송
      const keepFileIds = files.filter(f => !f.isNew && !f.isDeleted && f.id).map(f => f.id);
      formData.append('keep_file_ids', JSON.stringify(keepFileIds));

      // 신규 첨부파일
      files.filter(f => f.isNew && !f.isDeleted).forEach(f => {
        if (f.file) formData.append('files', f.file);
        else if (f instanceof File) formData.append('files', f);
      });
      // 기존 파일 중 File 객체인 경우
      files.filter(f => !f.isDeleted && f instanceof File).forEach(f => {
        formData.append('files', f);
      });

      const res = await workOrderApi.submitReport(workOrderId, formData);
      if (res.success) {
        let msg = '처리결과가 성공적으로 제출되었습니다.';
        if (isDraftAction) {
          msg = '처리결과가 임시저장되었습니다.';
        } else if (isRejectEdit) {
          msg = '보완된 처리결과가 성공적으로 재제출되었습니다.';
        }
        alert(res.message || msg);
        if (onSubmitted) onSubmitted(res.data);
        if (onSaved) onSaved(res.data);
        onClose();
      } else {
        alert(res.message || '처리결과 저장 중 오류가 발생했습니다.');
      }
    } catch (err) {
      console.error('[WorkOrderReportFormModal] save error:', err);
      alert(err.response?.data?.message || err.message || '처리결과 처리에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const getHeaderGradient = () => {
    return 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)';
  };

  const getModalTitle = () => {
    if (isRejectEdit) {
      return (
        <>
          <i className="fas fa-edit" />
          처리결과 보완 수정
        </>
      );
    }
    if (isDraftEdit) {
      return (
        <>
          <i className="fas fa-pencil-alt" />
          처리결과 이어쓰기 (임시저장)
        </>
      );
    }
    return (
      <>
        <i className="fas fa-file-signature" />
        처리결과 작성
      </>
    );
  };

  const modalContent = (
    <div
      className="wo-modal-overlay"
      style={{
        backgroundColor: isSubModal ? 'rgba(15, 23, 42, 0.25)' : 'rgba(0, 0, 0, 0.6)',
        zIndex: isSubModal ? 10005 : 9998
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="wo-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="wo-modal-content">

          {/* ── 1. 헤더 ── */}
          <div className="wo-modal-header" style={{ background: getHeaderGradient() }}>
            <h5 className="m-0 fw-bold fs-5 text-white d-flex align-items-center gap-2">
              {getModalTitle()}
            </h5>
            <div className="d-flex align-items-center gap-2">
              {isDraftEdit ? (
                <span className="badge bg-white text-primary px-2 py-1 fw-bold" style={{ fontSize: '0.75rem' }}>
                  {report?.report_seq || reportSeq}차 임시저장
                </span>
              ) : isFinalReport ? (
                <span className="badge bg-white px-2 py-1 fw-bold" style={{ color: '#0f766e', fontSize: '0.75rem' }}>
                  최종결과
                </span>
              ) : (
                <span className="badge bg-white px-2 py-1 fw-bold" style={{ color: '#0f766e', fontSize: '0.75rem' }}>
                  {report?.report_seq || reportSeq}차 결과
                </span>
              )}
              <button type="button" className="btn-close btn-close-white" onClick={onClose} style={{ boxShadow: 'none' }} />
            </div>
          </div>

          {/* ── 2. 폼 본문 (수직 Flex 레이아웃) ── */}
          <form
            onSubmit={(e) => { e.preventDefault(); handleSave(false); }}
            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
          >
            <div className="wo-modal-body">
              <div className="row g-3">



                {/* 관련 원문 업무지시 안내 & 보고서 상태 바 */}
                <div className="col-12">
                  <div
                    className="p-3 bg-white rounded-3 shadow-sm border d-flex align-items-center justify-content-between"
                    style={{
                      borderColor: '#e2e8f0',
                      fontSize: '0.85rem'
                    }}
                  >
                    {/* 좌측: 원문서 제목 (말줄임 시에만 툴팁, 밑줄 없음) */}
                    <div className="d-flex align-items-center gap-2 text-truncate pe-3" style={{ minWidth: 0, flex: 1 }}>
                      <i className="fas fa-file-alt flex-shrink-0" style={{ color: '#0d9488', fontSize: '0.95rem' }} />
                      <span className="text-secondary fw-bold flex-shrink-0">요청원문서 : </span>
                      <span
                        className="fw-bold text-truncate"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#2563eb';
                          e.currentTarget.style.textDecoration = 'underline';
                          if (e.currentTarget.scrollWidth > e.currentTarget.clientWidth) {
                            e.currentTarget.title = targetTitle || '';
                          } else {
                            e.currentTarget.removeAttribute('title');
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#1e40af';
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                        onClick={() => setViewWorkOrderOpen(true)}
                        style={{
                          color: '#1e40af',
                          cursor: 'pointer',
                          transition: 'color 0.15s ease'
                        }}
                      >
                        {targetTitle || '-'}
                      </span>
                    </div>

                    {/* 우측: 보고서 상태 (적당한 상하 볼륨감 있는 뱃지) */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <span className="text-secondary fw-bold">상태 : </span>
                      <span
                        className="badge fw-bold shadow-sm d-inline-flex align-items-center justify-content-center"
                        style={{
                          fontSize: '0.74rem',
                          padding: '4px 9px',
                          lineHeight: '1.2',
                          backgroundColor: isRejectEdit ? '#e11d48' : isDraftEdit ? '#0284c7' : '#0d9488',
                          color: '#ffffff',
                          borderRadius: '6px'
                        }}
                      >
                        {isRejectEdit ? '보완 요청' : isDraftEdit ? '임시저장' : '신규 등록'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 1. 보고서 제목 & 진척도 수정 (%) */}
                <div className="col-12 col-md-8">
                  <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                    <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-heading me-1 text-teal" style={{ color: '#0d9488' }} /> 처리결과 제목 <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <select
                        className="form-select fw-bold flex-shrink-0"
                        style={{
                          maxWidth: '145px',
                          fontSize: '0.82rem',
                          backgroundColor: isFinalReport ? '#ecfdf5' : '#f8fafc',
                          color: isFinalReport ? '#047857' : '#334155',
                          borderColor: isFinalReport ? '#a7f3d0' : '#cbd5e1',
                          cursor: isProgressReadOnly ? 'not-allowed' : 'pointer'
                        }}
                        value={isFinalReport ? 'final' : 'interim'}
                        onChange={(e) => handleReportTypeChange(e.target.value)}
                        disabled={isProgressReadOnly}
                      >
                        <option value="interim">
                          {`[${activeSeq}차 결과]`}
                        </option>
                        <option value="final">
                          [최종결과]
                        </option>
                      </select>
                      <input
                        type="text"
                        className="form-control"
                        value={reportTitle}
                        onChange={e => setReportTitle(e.target.value)}
                        placeholder="처리결과 상세 제목을 입력해 주세요"
                        required
                        style={{ fontSize: '0.85rem' }}
                      />
                    </div>
                  </div>
                </div>

                {/* 2단 우측: 업무 기간 (col-12 col-md-4) */}
                <div className="col-12 col-md-4">
                  <div
                    className="p-3 bg-white rounded-3 shadow-sm border h-100 d-flex flex-column justify-content-center"
                    style={{ borderColor: '#e2e8f0' }}
                  >
                    <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-calendar-alt me-1 text-teal" style={{ color: '#0d9488' }} /> 업무 기간
                    </label>
                    <div className="fw-bold" style={{ fontSize: '0.85rem', color: '#1e293b' }}>
                      {periodText}
                    </div>
                  </div>
                </div>

                {/* 3단 좌측: 진척도 (col-12 col-md-6, 50% 확장) */}
                <div className="col-12 col-md-6">
                  <div className="p-3 bg-white rounded-3 shadow-sm border h-100 d-flex flex-column justify-content-center" style={{ borderColor: '#e2e8f0' }}>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-chart-line me-1 text-teal" style={{ color: '#0d9488' }} /> 진척도
                      </label>
                      <div className="d-flex align-items-center gap-1">
                        {isFinalReport ? (
                          <span className="badge bg-success" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>
                            최종 완료
                          </span>
                        ) : (
                          <span className="badge bg-primary" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>
                            {isRejectEdit ? '보완' : '중간'}
                          </span>
                        )}
                        {isProgressReadOnly && (
                          <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: '4px', backgroundColor: '#f1f5f9', color: '#64748b', fontWeight: 600 }}>
                            조회 전용
                          </span>
                        )}
                      </div>
                    </div>
                    {isProgressReadOnly ? (
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <div className="position-relative flex-grow-1" style={{ height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${progressRate}%`,
                              height: '100%',
                              backgroundColor: progressRate === 100 ? '#10b981' : '#2563eb',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                        <span
                          className="fw-bold"
                          style={{
                            minWidth: '45px',
                            textAlign: 'right',
                            fontSize: '0.85rem',
                            color: progressRate === 100 ? '#10b981' : '#2563eb'
                          }}
                        >
                          {progressRate}%
                        </span>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <div className="position-relative flex-grow-1 d-flex align-items-center" style={{ height: '24px' }}>
                          {/* 바탕 회색 트랙 */}
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              height: '6px',
                              backgroundColor: '#e2e8f0',
                              borderRadius: '3px',
                              pointerEvents: 'none'
                            }}
                          />
                          {/* 진척도 채움 바 */}
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              width: `${progressRate}%`,
                              height: '6px',
                              backgroundColor: progressRate === 100 ? '#10b981' : '#2563eb',
                              borderRadius: '3px',
                              pointerEvents: 'none',
                              transition: 'background-color 0.15s ease'
                            }}
                          />
                          {/* 조작용 투명 레인지 슬라이더 */}
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={progressRate}
                            onChange={e => handleProgressChange(Number(e.target.value))}
                            style={{
                              position: 'relative',
                              width: '100%',
                              margin: 0,
                              background: 'transparent',
                              cursor: 'pointer',
                              accentColor: progressRate === 100 ? '#10b981' : '#2563eb',
                              zIndex: 2
                            }}
                          />
                        </div>
                        <input
                          type="number"
                          className="form-control form-control-sm text-center fw-bold"
                          min="0"
                          max="100"
                          value={progressRate}
                          onChange={e => handleProgressChange(Number(e.target.value) || 0)}
                          style={{
                            width: '56px',
                            fontSize: '0.82rem',
                            color: progressRate === 100 ? '#10b981' : '#2563eb',
                            borderColor: progressRate === 100 ? '#a7f3d0' : '#bfdbfe'
                          }}
                        />
                        <span className="small text-muted fw-bold">%</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div
                    className="p-3 bg-white rounded-3 shadow-sm border h-100 d-flex align-items-center"
                    style={{ borderColor: isPublic ? '#e2e8f0' : '#fde68a' }}
                  >
                    <div className="d-flex align-items-center gap-3 w-100">
                      <div className="form-check form-switch m-0 p-0 d-flex align-items-center flex-shrink-0">
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          id="isPublicSwitch"
                          checked={isPublic}
                          onChange={e => setIsPublic(e.target.checked)}
                          style={{
                            cursor: 'pointer',
                            marginTop: 0,
                            transform: 'scale(1.15)',
                            transformOrigin: 'center center'
                          }}
                        />
                      </div>
                      <div className="d-flex flex-column justify-content-center flex-grow-1" style={{ minWidth: 0 }}>
                        <label
                          className="form-check-label fw-bold text-truncate mb-0"
                          htmlFor="isPublicSwitch"
                          style={{
                            cursor: 'pointer',
                            fontSize: '0.88rem',
                            color: isPublic ? '#1e293b' : '#b45309',
                            lineHeight: '1.2'
                          }}
                        >
                          {isPublic ? '🌐 담당자 및 참조자에게 처리결과 전체 공개 (기본)' : '🔒 비공개 처리결과 (요청자와 본인만 열람)'}
                        </label>
                        <div className="text-truncate mt-1" style={{ fontSize: '0.78rem', color: isPublic ? '#64748b' : '#d97706', lineHeight: '1.2' }}>
                          {isPublic
                            ? '모든 담당자와 참조자가 내용 및 첨부파일 열람 가능'
                            : '요청자와 작성자만 내용 및 첨부파일 열람 가능'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. 보고 상세 내용 */}
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0', height: editorHeight, transition: 'height 0.3s ease' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-align-left me-1 text-teal" style={{ color: '#0d9488' }} /> 처리결과 상세 내용 <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-secondary fw-semibold" style={{ fontSize: '0.8rem' }}>에디터 높이 :</span>
                        <select
                          className="form-select form-select-sm text-secondary"
                          style={{ width: '130px', fontSize: '0.8rem', cursor: 'pointer' }}
                          value={editorHeight}
                          onChange={(e) => setEditorHeight(e.target.value)}
                        >
                          <option value="400px">기본 (400px)</option>
                          <option value="600px">넓게 (600px)</option>
                          <option value="800px">아주 넓게 (800px)</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                      <div className="border p-1 rounded bg-white h-100 d-flex flex-column">
                        {editor && <EditorToolbar editor={editor} />}
                        <div className="flex-grow-1 overflow-auto bg-white" style={{ minHeight: 0 }}>
                          <EditorContent editor={editor} className="editor-content h-100" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. 산출물 및 첨부파일 */}
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0' }}>
                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-paperclip me-1 text-teal" style={{ color: '#0d9488' }} /> 산출물 및 첨부파일
                    </label>
                    <div className="flex-grow-1 overflow-auto bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                      <FileAttachment files={files} setFiles={setFiles} mode="edit" />
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* ── 3. 푸터 (모달 맨 하단 고정: [임시저장] + [제출] + [취소] 맨 우측 순서) ── */}
            <div className="wo-modal-footer d-flex align-items-center justify-content-end gap-2 w-100">
              {/* 맨 왼쪽: 업무지시 조회 버튼 */}
              <button
                type="button"
                className="btn btn-outline-primary btn-sm px-3 fw-bold me-auto shadow-sm"
                onClick={() => setViewWorkOrderOpen(true)}
                title="원문 업무협조 내용을 조회합니다."
              >
                <i className="fas fa-file-alt me-1" />
                업무협조 조회
              </button>

              {/* 임시저장 버튼 */}
              {!isRejectEdit && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3 fw-bold shadow-sm"
                  onClick={() => handleSave(true)}
                  disabled={submitting}
                  title="현재 작성 내용을 임시저장합니다."
                >
                  <i className="fas fa-save me-1" />
                  임시저장
                </button>
              )}

              {/* 제출 버튼 */}
              <button
                type="submit"
                className="btn btn-sm px-3 fw-bold shadow-sm text-white"
                style={{ backgroundColor: '#0d9488', borderColor: '#0d9488' }}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    처리 중...
                  </>
                ) : isRejectEdit ? (
                  <>
                    <i className="fas fa-paper-plane me-1" />
                    보완결과 제출
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-1" />
                    처리결과 제출
                  </>
                )}
              </button>

              {/* 취소 버튼 (맨 오른쪽) */}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-3 fw-bold shadow-sm"
                onClick={onClose}
                disabled={submitting}
              >
                취소
              </button>
            </div>
          </form>

        </div>
      </div>

      {/* ── 원문 업무지시 조회 팝업 (투명 배경 서브모달) ── */}
      {viewWorkOrderOpen && (
        <WorkOrderFormModal
          open={viewWorkOrderOpen}
          mode="view"
          workOrderId={workOrderId || report?.work_order_id || report?.workOrder?.id}
          isSubModal={true}
          zIndex={10020}
          onClose={() => setViewWorkOrderOpen(false)}
        />
      )}
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default WorkOrderReportFormModal;
